import { loadScript } from 'helpers/dom'
import debounce from 'lodash.debounce'

const selectors = {
  VIDEO_PLAYER_SELECTOR: 'video-player',
  VIDEO_THUMBNAIL_SELECTOR: '.js-video-player-thumbnail',
  VIDEO_THUMBNAIL_BUTTON_SELECTOR: '.js-video-player-thumbnail-button',
  HIDDEN_CLASS: 'opacity-0',
  PLAYING_CLASS: 'is-playing',
  LOADING_CLASS: 'is-loading',
  PAUSING_CLASS: 'is-pausing',
  VIDEO_TYPES: {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    MP4: 'mp4'
  },
  STATES: {
    PLAYING: 'playing',
    PAUSING: 'pausing',
    LOADING: 'loading'
  },
  DEBOUNCE_TIME: 300
}

export default class VideoPlayer extends HTMLElement {
  constructor() {
    super()
    this.player = null
    this.element = document.createElement('div')
    this.type = this.getAttribute('data-video-type') || selectors.VIDEO_TYPES.MP4
    this.thumbnailEl = null
    this.isLoaded = false
    this.src = this.getAttribute('src') || null
    this.status = {
      play: null,
      error: null
    }
    this._initId = 0
    this.pausedByScroll = false
    this.options = {
      autoplay: this.hasAttribute('autoplay'),
      controls: this.hasAttribute('controls'),
      playsinline: this.hasAttribute('playsinline'),
      muted: this.hasAttribute('muted'),
      loop: this.hasAttribute('loop'),
      origin: window.location.origin,
      thumbnail: this.getAttribute('data-thumbnail'),
      playIntersection: this.hasAttribute('data-play-intersection'),
      lazyload: this.hasAttribute('lazyload'),
      customButton: this.hasAttribute('custom-button'),
      showInitialThumbnail: this.hasAttribute('show-initial-thumbnail')
    }

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
      this.appendChild(this.element)
    }

    if (!this.src) {
      this.status.error = 'No video src'
    }

    if (this.options.playIntersection) {
      this.initWhenVisible()
    }
    this.unload = this.unload.bind(this)
    this.play = this.play.bind(this)
    this.pause = this.pause.bind(this)
    this.initWhenVisible = this.initWhenVisible.bind(this)

    if (this.hasAttribute('auto-init')) {
      this.init()
    }

    if (this.hasAttribute('lazyload')) {
      this.lazyload()
    }

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return
      if (this._handlePause) this._handlePause.cancel()

      if (this.player) {
        if (this.options.autoplay) {
          this.pausedByScroll = false

          if (this.type === selectors.VIDEO_TYPES.MP4 && this.player) {
            this.player.load()

            this.player.addEventListener('canplay', () => this.play(), {
              once: true
            })
          } else {
            this.play()
          }

          return
        }

        return
      }

      this.resetPlayer()
      this.isLoaded = true
      this.init()

      if (this.options.autoplay && this.type === selectors.VIDEO_TYPES.MP4 && this.player) {
        this.player.load()
        this.play()
      }
    })

    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.status.play) {
        this.pausedByScroll = true
        this.pause()
      }
    })
  }

  get getVideoID() {
    if (this.getAttribute('data-external-id')) return this.getAttribute('data-external-id')

    if (/youtu\.?be/.test(this.src)) {
      const patterns = [
        /youtu\.be\/([^#&?]{11})/, // youtu.be/<id>
        /\?v=([^#&?]{11})/, // ?v=<id>
        /&v=([^#&?]{11})/, // &v=<id>
        /embed\/([^#&?]{11})/, // embed/<id>
        /\/v\/([^#&?]{11})/ // /v/<id>
      ]

      for (let i = 0; i < patterns.length; ++i) {
        if (patterns[i].test(this.src)) {
          return patterns[i].exec(this.src)[1]
        }
      }
    }

    if (/^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.test(this.src)) {
      const parseURL = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/.exec(this.src)

      return parseURL[5]
    }

    return null
  }

  initWhenVisible() {
    this.observeVisibility(this).then((isVisible) => {
      if (!this.isLoaded && isVisible) {
        if (this.options.autoplay && this.options.thumbnail) {
          this.initThumbnail()
        }
        this.src && this.type && this.init()
        this.isLoaded = true
      }
    })
  }

  resetPlayer() {
    if (this.player) {
      try {
        if (this.type === selectors.VIDEO_TYPES.YOUTUBE && this.player.destroy) {
          this.player.destroy()
        } else if (this.type === selectors.VIDEO_TYPES.VIMEO && this.player.destroy) {
          this.player.destroy()
        }
      } catch (e) {}
    }

    if (this.vimeoContainer) {
      this.vimeoContainer.remove()
      this.vimeoContainer = null
    }

    this.querySelectorAll('iframe, video').forEach(el => el.remove())

    const thumbnail = this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)

    if (thumbnail) thumbnail.remove()

    if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
      this.element = document.createElement('div')
      this.appendChild(this.element)
    }

    this._initId++
    this.player = null
    this.videoEl = null
    this.isLoaded = false
    this.status = { play: null, error: null }
    this.pausedByScroll = false
    this._initializing = false
    this.setStatePlayer(null)
  }

  unload() {
    switch (this.type) {
      case 'youtube':
        this.player.clearVideo()
        break
      case 'vimeo':
        this.player.unload()
        break
    }
  }

  initThumbnail() {
    if (this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)) return

    let htmlButtonPlay = ''
    let buttonVideoClass = 'h-12 w-12 rounded-full'

    if (this.options.customButton) {
      htmlButtonPlay = `
        <div class="flex items-center gap-4">
          <svg class="icon w-[22px] h-[22px] aspect-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 22 22">
            <path stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.602 10.416 7.236 2.853a.688.688 0 0 0-1.048.585v15.125a.687.687 0 0 0 1.048.584l12.366-7.562a.68.68 0 0 0 0-1.17v0Z"/>
          </svg>
          <span class="text-white text-link font-medium uppercase">Play Video</span>
        </div>
      `

      buttonVideoClass = 'top-4 left-4 [.is-loading_&]:top-auto [.is-loading_&]:left-auto'
    } else {
      htmlButtonPlay = `
        <svg width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
          <path d="m28.512 15.15-17.987-11A1 1 0 0 0 9 5v22a1 1 0 0 0 1.525.85l17.987-11a.987.987 0 0 0 0-1.7v0Z" stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    }

    const html = `
      <div class="thumbnail-video js-video-player-thumbnail absolute left-0 top-0 w-full h-full flex items-center justify-center transition-opacity z-9">
      ${this.options.thumbnail ? `<img src="${this.options.thumbnail}" loading="lazy" class="w-full h-full object-cover">` : ''}
          <button type="button" class="js-video-player-thumbnail-button absolute bg-primary flex z-50 items-center justify-center ${buttonVideoClass}">
            <div class="[.is-loading_&]:hidden [.is-pausing_&]:hidden">
              ${htmlButtonPlay}
            </div>
            <div class="hidden [.is-pausing_&]:block">
              <svg width="36" height="36" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                <g stroke="#FFFFFF" stroke-linecap="round" stroke-linejoin="round"><path d="M25 5h-4.5a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1H25a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1ZM11.5 5H7a1 1 0 0 0-1 1v20a1 1 0 0 0 1 1h4.5a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1Z"/>
                </g>
              </svg>
            </div>
            <div class="hidden [.is-loading_&]:block">
              <span class="button-loading button-loading-large"></span>
            </div>
          </button>
        </div>
      </div>
      `

    this.thumbnailEl = html
    this.insertAdjacentHTML('beforeend', this.thumbnailEl)

    const buttonEl = this.querySelector(selectors.VIDEO_THUMBNAIL_BUTTON_SELECTOR)

    if (this.options.autoplay) {
      this.setStatePlayer(selectors.STATES.LOADING)
    }
    buttonEl.addEventListener('click', (e) => {
      e.stopPropagation()
      e.preventDefault()

      if (!this.player) {
        this.setStatePlayer(selectors.STATES.LOADING)

        if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
          this.setupYTPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.VIMEO) {
          this.setupVimeoPlayer()
        }

        if (this.type === selectors.VIDEO_TYPES.MP4) {
          this.setupMP4Player()
        }
      } else {
        if (!this.status.play) {
          this.play()
        } else {
          this.pause()
        }
      }
    })
  }

  initYoutubePlayer() {
    if (!this.options.autoplay) {
      if (!this.options.thumbnail && this.getVideoID) {
        this.options.thumbnail = `https://img.youtube.com/vi/${this.getVideoID}/maxresdefault.jpg`
      }
      this.initThumbnail()
    } else {
      YT.ready(() => this.setupYTPlayer())
    }
  }

  initVimeoPlayer() {
    if (!this.options.autoplay) {
      if (!this.options.thumbnail && this.getVideoID) {
        fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${this.getVideoID}`)
          .then(res => res.json())
          .then(data => {
            if (data.thumbnail_url) {
              this.options.thumbnail = data.thumbnail_url
            }
            this.initThumbnail()
          })
          .catch(() => this.initThumbnail())

        return
      }
      this.initThumbnail()
    } else {
      this.setupVimeoPlayer()
    }
  }

  initMP4Player() {
    if (this.querySelector('video')) return

    if (!this.options.autoplay) {
      this.setupMP4Player(false)

      if (!this.options.thumbnail) {
        this.player.preload = 'metadata'

        this.player.addEventListener('loadedmetadata', () => {
          this.player.currentTime = 0.001
        }, { once: true })
      }

      this.initThumbnail()
    } else {
      this.setupMP4Player()
      this.player.addEventListener('canplay', () => {
        this.play()
      }, { once: true })
    }
  }

  async initByType(type) {
    switch (type) {
      case selectors.VIDEO_TYPES.YOUTUBE:
        this.initYoutubePlayer()
        break
      case selectors.VIDEO_TYPES.VIMEO:
        this.initVimeoPlayer()
        break
      case selectors.VIDEO_TYPES.MP4:
        this.initMP4Player()
        break
    }
  }

  async loadPlayerAPIs(type) {
    let src = ''

    if (type === selectors.VIDEO_TYPES.YOUTUBE && !window.YT) {
      src = 'https://www.youtube.com/iframe_api'
    } else if (type === selectors.VIDEO_TYPES.VIMEO && !window.Vimeo) {
      src = 'https://player.vimeo.com/api/player.js'
    } else {
      return
    }

    if (!VideoPlayer._apiPromises) VideoPlayer._apiPromises = {}

    if (!VideoPlayer._apiPromises[src]) {
      VideoPlayer._apiPromises[src] = loadScript(src)
    }

    return VideoPlayer._apiPromises[src]
  }

  setupYTPlayer() {
    const options = {
      videoId: this.getVideoID,
      playerVars: {
        autoplay: 0,
        controls: this.options.controls ? 1 : 0,
        height: '100%',
        width: '100%',
        playsinline: this.options.playsinline ? 1 : 0,
        loop: this.options.loop ? 1 : 0
      },
      events: {
        onError: () => {
          this.status.error = new Error('player could not be built')
        },
        onStateChange: (e) => this._onYouTubeStateChange(e),
        onReady: (e) => this._onYoutubeReadyState(e)
      }
    }

    if (YT.loaded) {
      this.player = new window.YT.Player(this.element, options)
    }
  }

  setupVimeoPlayer() {
    if (this.player) return

    const options = {
      id: this.getVideoID,
      ...this.options,
      autopause: false
    }

    this.vimeoContainer = document.createElement('div')
    this.vimeoContainer.style.width = '100%'
    this.vimeoContainer.style.height = '100%'
    this.appendChild(this.vimeoContainer)

    this.player = new window.Vimeo.Player(this.vimeoContainer, options)

    const currentInitId = this._initId

    if (!this.options.autoplay) {
      this.player.ready().then(() => {
        if (this._initId !== currentInitId) return
        this.stretchIframe()
        this.play()
        this.setStatePlayer(selectors.STATES.PLAYING)
        this.displayThumbnail(false)
      })
    } else {
      this.player.ready().then(() => {
        if (this._initId !== currentInitId) return
        this.stretchIframe()
        this.play()
        this.displayThumbnail(false)
      })
    }

    this.player.on('play', () => this.onPlay())
    this.player.on('pause', () => this.onPause())
    this.player.on('ended', () => this.onEnd)
  }

  setupMP4Player(autoplay = true) {
    this.videoEl = document.createElement('video')
    this.videoEl.className = 'w-full h-full object-cover'
    this.videoEl.muted = this.options.muted
    this.videoEl.playsInline = this.options.playsinline
    this.videoEl.autoplay = this.options.autoplay
    this.videoEl.src = this.src
    this.player = this.videoEl
    this.appendChild(this.videoEl)

    if (autoplay) {
      this.displayThumbnail(false)
    }

    this.player.controls = autoplay ? this.options.controls : false
    this.player.loop = this.options.loop
    this.player.preload = 'auto'

    if (this.options.muted) {
      this.videoEl.setAttribute('muted', '')
    }

    this.player.addEventListener('play', () => {
      if (!this.videoEl.paused && this.videoEl.currentTime > 0 && !this.videoEl.ended && this.videoEl.readyState > this.videoEl.HAVE_CURRENT_DATA) {
        this.onPlay()
      }
    })
    this.player.addEventListener('pause', () => this.onPause())
    this.player.addEventListener('ended', () => this.onEnd())
  }

  play() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.playVideo()
      } else {
        if (this.options.muted) {
          this.player.muted = true
        }

        const playPromise = this.player.play()

        if (playPromise !== undefined) {
          playPromise.then(() => {
            this.onPlay()
          }).catch(error => {
            this.status.error = error
          })
        }
      }
    } catch (error) {
      this.status.error = error
    }
  }

  pause() {
    try {
      if (!this.player) return

      if (this.type === selectors.VIDEO_TYPES.YOUTUBE) {
        this.player.pauseVideo()
      } else {
        this.player.pause()
      }
    } catch (error) {
      this.status.error = error
    }
  }

  onPlay() {
    this.status.play = true
    this.dispatchEvent(new CustomEvent('play'))
    this.setStatePlayer(selectors.STATES.PLAYING)
    this.displayThumbnail(false)
    this.stretchIframe()
  }

  onPause() {
    if (!this.status.play) return

    this.pause()
    this.status.play = false
    this.dispatchEvent(new CustomEvent('pause'))

    if (this.pausedByScroll) {
      this.pausedByScroll = false
      this.setStatePlayer(null)
      this.displayThumbnail(true)
    } else {
      this.setStatePlayer(selectors.STATES.PAUSING)

      if (!this.options.showInitialThumbnail) {
        this.displayThumbnail(true)
      }
    }
  }

  onEnd() {
    if (this.options.loop) {
      this.play()
    } else {
      this.status.play = false
      this.dispatchEvent(new CustomEvent('ended'))
      this.displayThumbnail(true)
      this.setStatePlayer(null)
    }
  }

  setStatePlayer(status) {
    switch (status) {
      case selectors.STATES.PLAYING:
        this.classList.add(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.PAUSING:
        this.classList.add(selectors.PAUSING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.LOADING_CLASS)
        break
      case selectors.STATES.LOADING:
        this.classList.add(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
        break
      default:
        this.classList.remove(selectors.LOADING_CLASS)
        this.classList.remove(selectors.PLAYING_CLASS)
        this.classList.remove(selectors.PAUSING_CLASS)
    }
  }

  displayThumbnail(status) {
    const el = this.querySelector(selectors.VIDEO_THUMBNAIL_SELECTOR)

    if (!el) return

    if (status) {
      el.classList.remove(selectors.HIDDEN_CLASS)
      el.classList.add('visible')
      el.classList.remove('invisible')
    } else {
      el.classList.add(selectors.HIDDEN_CLASS)
      el.classList.remove('visible')
      el.classList.add('invisible')
    }
  }

  stretchIframe() {
    if (!this.classList.contains('stretch-video')) return

    const iframe = this.querySelector('iframe')

    if (!iframe) return

    const containerWidth = this.clientWidth
    const containerHeight = this.clientHeight

    if (!containerWidth || !containerHeight) {
      Object.assign(iframe.style, {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '300%',
        height: '300%',
        transform: 'translate(-50%, -50%)'
      })

      return
    }

    const containerRatio = containerWidth / containerHeight
    const videoRatio = 16 / 9

    let width, height

    if (containerRatio < videoRatio) {
      width = `${(videoRatio / containerRatio) * 100}%`
      height = '100%'
    } else {
      width = '100%'
      height = `${(containerRatio / videoRatio) * 100}%`
    }

    Object.assign(iframe.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width,
      height,
      transform: 'translate(-50%, -50%)'
    })
  }

  _onYouTubeStateChange(e) {
    const stateMap = {
      '-1': 'unstarted',
      0: 'ended',
      1: 'playing',
      2: 'pause',
      3: 'buffering',
      5: 'cued'
    }
    const state = stateMap[e.data.toString()]
    const eventMethodMap = {
      ended: this.onEnd,
      pause: this.onPause,
      playing: this.onPlay
    }

    if (eventMethodMap[state]) {
      const method = eventMethodMap[state]

      if (method) {
        method.call(this)
      }
    }
  }

  _onYoutubeReadyState() {
    this.stretchIframe()

    if (!this.options.autoplay) {
      this.setStatePlayer(selectors.STATES.PLAYING)
    }

    this.play()
    this.displayThumbnail(false)

    if (this.options.muted) {
      this.player.mute()
    }
  }

  observeVisibility(element) {
    return new Promise((resolve) => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting

          resolve(isVisible)
        })
      })

      observer.observe(element)
    })
  }

  lazyload() {
    this._handlePause = debounce(() => {
      if (this.status.play) {
        this.pausedByScroll = true
        this.pause()
      }
    }, selectors.DEBOUNCE_TIME)

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this._handlePause.cancel()

          if (!this.isLoaded) {
            if (this.options.thumbnail) {
              this.initThumbnail()
            }
            this.src && this.type && this.init()
            this.isLoaded = true
          }

          if (this.options.autoplay && this.isLoaded) {
            this.displayThumbnail(false)
            this.play()
          }
        } else {
          this._handlePause()
        }
      })
    }, { rootMargin: '314px' })

    observer.observe(this)
  }

  async init() {
    if (this._initializing) return

    this._initializing = true

    try {
      this.type !== selectors.VIDEO_TYPES.MP4 && (await this.loadPlayerAPIs(this.type))
      this.initByType(this.type)
    } finally {
      this._initializing = false
    }
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer)
}
